/** Shared variables for GET_NOTIFICATIONS. sinceDate, untilDate, and cursor are non-null. */
export const getNotificationQueryVariables = (userId: string) => {
  const untilDate = new Date().toISOString();
  const recentCutoff = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
  return {
    sinceDate: "1970-01-01T00:00:00.000Z",
    untilDate,
    recentCutoff,
    pageSize: 1,
    cursor: "9999-12-31T23:59:59.999Z",
    userId,
  };
};

type NotificationFeedSlice = {
  notification?: string | null;
};

type NotificationGlobalSlice = {
  notificationCount?: { recent?: number; past?: number };
  notification?: {
    reSettingNotificationCount?: boolean;
    newNotification?: string;
    lastAlertedFeedMs?: number;
  };
};

type MergeNotificationOptions = {
  /** Set baseline timestamp on load without playing sound/toast. */
  baselineOnly?: boolean;
};

type NotificationQueryResult = {
  current?: NotificationFeedSlice[];
  past?: NotificationFeedSlice[];
  all?: NotificationFeedSlice[];
  currentCount?: { aggregate?: { count?: number | null } | null } | null;
  pastCount?: { aggregate?: { count?: number | null } | null } | null;
};

const aggregateOrLength = (
  aggregate: NotificationQueryResult["currentCount"],
  rows: NotificationFeedSlice[] | undefined,
) => {
  const count = aggregate?.aggregate?.count;
  return typeof count === "number" ? count : (rows || []).length;
};

const getNewestFeedTimestampMs = (
  notificationData: NotificationQueryResult | null | undefined,
): number => {
  const items = [
    ...(notificationData?.all || []),
    ...(notificationData?.current || []),
    ...(notificationData?.past || []),
  ];
  return items.reduce((max, item) => {
    const ms = item?.notification ? new Date(item.notification).getTime() : NaN;
    return Number.isFinite(ms) && ms > max ? ms : max;
  }, 0);
};

/** Merge GET_NOTIFICATIONS results; alert only when a newer feed arrives than last alerted. */
export const mergeNotificationQueryIntoGlobalState = <T extends NotificationGlobalSlice>(
  globalState: T,
  notificationData: NotificationQueryResult | null | undefined,
  options?: MergeNotificationOptions,
): T => {
  const tempData = { ...globalState };
  const newRecent = aggregateOrLength(notificationData?.currentCount, notificationData?.current);
  const newPast = aggregateOrLength(notificationData?.pastCount, notificationData?.past);
  const newestFeedMs = getNewestFeedTimestampMs(notificationData);
  const lastAlertedFeedMs = globalState.notification?.lastAlertedFeedMs || 0;
  const hasNewFeed = newestFeedMs > lastAlertedFeedMs;

  tempData.notificationCount = {
    ...globalState.notificationCount,
    recent: newRecent,
    past: newPast,
  };

  tempData.notification = {
    ...globalState.notification,
    reSettingNotificationCount: !globalState.notification?.reSettingNotificationCount,
  };

  if (options?.baselineOnly) {
    if (newestFeedMs > 0) {
      tempData.notification.lastAlertedFeedMs = newestFeedMs;
    }
  } else if (hasNewFeed) {
    tempData.notification.newNotification = new Date().toISOString();
    tempData.notification.lastAlertedFeedMs = newestFeedMs;
  }

  return tempData;
};

/** Re-fetch notifications shortly after an action so new feeds are detected reliably. */
export const scheduleNotificationSyncRetries = (
  sync: () => void,
  delaysMs: number[] = [0, 800, 2000, 4000],
) => {
  delaysMs.forEach((delay) => {
    window.setTimeout(sync, delay);
  });
};
